import { useEffect, useState, useMemo } from 'react';
import type { JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { styled } from 'styled-components';
import { isNil, isNotNil } from '@wistia/type-guards';
import { Box } from '../Box';
import { Heading } from '../Heading';
import { Text } from '../Text';
import { Icon } from '../Icon';
import { ButtonGroup } from '../ButtonGroup';
import { IconButton } from '../IconButton';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import { useElementObserver } from '../../hooks/useElementObserver';

export const BREAKPOINT_WIDTH = 600;
const VERTICAL_BREAKPOINT_WIDTH = 284;
const MIN_IMAGE_WIDTH = 400;

type StyledFeatureCardProps = {
  $colorScheme: ColorSchemeTypes;
  $contentHeight: number;
  $width?: number | undefined;
};

const StyledFeatureCard = styled.div<StyledFeatureCardProps>`
  --wui-feature-card-padding: var(--wui-space-04);
  --wui-feature-card-content-height: ${({ $contentHeight }) => $contentHeight}px;

  display: flex;
  flex-direction: row;
  background: var(--wui-color-bg-surface-secondary);
  border-radius: var(--wui-border-radius-03);
  position: relative;
  overflow: hidden;
  align-items: center;

  &[data-wui-feature-card-content-prominence='secondary'] {
    --wui-feature-card-padding: var(--wui-space-05);

    box-shadow: inset 0 0 0 2px var(--wui-color-border);
  }

  &:has([data-wui-feature-card-image]) {
    --wui-feature-card-padding: var(--wui-space-05);
  }

  &[data-wui-feature-card-content-prominence='small'] {
    --wui-feature-card-padding: var(--wui-space-04);
  }

  &[data-wui-feature-card-orientation='vertical'] {
    flex-direction: column;
    align-items: stretch;
  }

  ${({ $colorScheme }) => getColorScheme($colorScheme)};
`;

export type FeatureCardProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * The main text content displayed in the feature card
   */
  bodyText: ReactNode;
  /**
   * Optional heading text displayed at the top of the feature card content
   */
  headingText?: string;
  /**
   * Optional `Icon` component to use. Must be an instance of [Icon](../?path=/docs/components-icon--docs)
   */
  icon?: JSX.Element;
  /**
   * Controls the visual prominence of the feature card
   */
  prominence?: 'primary' | 'secondary';
  colorScheme?: ColorSchemeTypes;
  /**
   * Optional image element to display in the feature card
   */
  image?: JSX.Element | undefined;
  /**
   * Controls the layout direction. If set to 'auto', the feature card will switch between horizontal and vertical based on the container width.
   */
  orientation?: 'auto' | 'horizontal' | 'vertical';
  /**
   * Primary call-to-action element (typically a [Button]() of the `solid` variant)
   */
  primaryAction?: ReactNode;
  /**
   * Secondary call-to-action element (typically a [Button]() of the `outline` variant)
   */
  secondaryAction?: ReactNode;
  /**
   * Optional callback function when the close button is clicked. If not provided, the close button will not be displayed.
   */
  onClose?: () => void;
};

/**
 * The `FeatureCard` component is for displaying contextual information to the user such as alerts or feature announcements.
 */
export const FeatureCard = ({
  bodyText,
  colorScheme = 'inherit',
  headingText,
  icon,
  image,
  onClose,
  orientation = 'horizontal',
  primaryAction,
  prominence = 'primary',
  secondaryAction,
  ...props
}: FeatureCardProps): JSX.Element => {
  const [containerRef, { width }] = useElementObserver<HTMLDivElement>();
  const [contentRef, { height: contentHeight }] = useElementObserver<HTMLDivElement>();
  const [isSmallContainer, setIsSmallContainer] = useState(false);
  const [isVerticalLayout, setIsVerticalLayout] = useState(orientation === 'vertical');

  useEffect(() => {
    const breakpoint = orientation === 'vertical' ? VERTICAL_BREAKPOINT_WIDTH : BREAKPOINT_WIDTH;
    setIsSmallContainer(width <= breakpoint);

    if (orientation === 'auto') {
      setIsVerticalLayout(width <= BREAKPOINT_WIDTH);
    }
  }, [width, orientation]);

  const hasImage = isNotNil(image);
  const shouldShowImage = hasImage && (isVerticalLayout || width >= MIN_IMAGE_WIDTH);

  const iconPosition = useMemo(() => {
    if (isNil(icon)) {
      return 'none';
    }
    if (isSmallContainer) {
      return shouldShowImage ? 'inline' : 'above';
    }
    return prominence === 'secondary' ? 'inline' : 'above';
  }, [icon, isSmallContainer, shouldShowImage, prominence]);

  const hasActions = isNotNil(primaryAction) || isNotNil(secondaryAction);

  const contentDirection = useMemo(() => {
    if (orientation === 'horizontal' && !hasActions) {
      return 'row';
    }
    return !shouldShowImage && prominence === 'primary' && !isSmallContainer && !isVerticalLayout
      ? 'row'
      : 'column';
  }, [orientation, shouldShowImage, prominence, isSmallContainer, isVerticalLayout, hasActions]);

  const headingVariant = isSmallContainer || prominence === 'primary' ? 'heading5' : 'heading3';
  const textVariant = prominence === 'primary' || isSmallContainer ? 'body3' : 'body2';
  const buttonSize = isSmallContainer ? 'sm' : 'md';

  return (
    <StyledFeatureCard
      {...props}
      ref={containerRef}
      $colorScheme={colorScheme}
      $contentHeight={contentHeight}
      $width={!isSmallContainer && !isVerticalLayout ? width : undefined}
      data-wui-feature-card-content-prominence={isSmallContainer ? 'small' : prominence}
      data-wui-feature-card-orientation={isVerticalLayout ? 'vertical' : 'horizontal'}
    >
      {shouldShowImage ? image : null}

      <Box
        ref={contentRef}
        alignItems="stretch"
        direction={contentDirection}
        flexMode="grow"
        gap="space-04"
        style={{ padding: 'var(--wui-feature-card-padding)' }}
      >
        {iconPosition === 'above' && (
          <Box
            alignSelf="stretch"
            direction="column"
            justifyContent="center"
          >
            {icon}
          </Box>
        )}

        <Box
          direction="column"
          flexMode={contentDirection === 'row' ? 'grow' : 'initial'}
          gap={iconPosition === 'inline' ? 'space-01' : 'space-00'}
          justifyContent="center"
        >
          {isNotNil(headingText) ? (
            <Heading variant={headingVariant}>
              {iconPosition === 'inline' && <>{icon} </>}
              {headingText}
            </Heading>
          ) : null}

          <Text variant={textVariant}>{bodyText}</Text>
        </Box>

        {hasActions ? (
          <ButtonGroup
            buttonSize={buttonSize}
            collapseOnSmallScreens={false}
            style={{ paddingRight: 'var(--wui-space-02)' }}
          >
            {isNotNil(primaryAction) && primaryAction}
            {isNotNil(secondaryAction) && secondaryAction}
          </ButtonGroup>
        ) : null}
      </Box>

      {isNotNil(onClose) && (
        <IconButton
          label="Close"
          onClick={onClose}
          size="sm"
          style={{
            position: 'absolute',
            top: 'var(--wui-space-01)',
            right: 'var(--wui-space-01)',
          }}
          variant="soft"
        >
          <Icon type="close" />
        </IconButton>
      )}
    </StyledFeatureCard>
  );
};

FeatureCard.displayName = 'FeatureCard';
